import { useCallback } from 'react'
import { AbstractConnector } from '@web3-react/abstract-connector'
import { useWeb3React, UnsupportedChainIdError } from '@web3-react/core'
import { NoEthereumProviderError, UserRejectedRequestError as UserRejectedRequestErrorInjected } from '@web3-react/injected-connector'
import { useAddPopup } from '../state/application/hooks'
import { NETWORK_CHAIN_ID } from '../connectors'

/**
 * EIP-1193 / JSON-RPC error codes a wallet can reject an activation with.
 * Wallets reject with a *plain object* (`{ code, message }`), not an `Error`
 * instance, so these never satisfy `instanceof Error` and must be read directly.
 */
const WALLET_ERROR_MESSAGES: { [code: number]: string } = {
  4001: 'Connection request was rejected in your wallet.',
  4100: 'Your wallet has not authorised this account. Unlock it and try again.',
  4200: 'Your wallet does not support this connection method.',
  4900: 'Your wallet is disconnected. Reconnect it and try again.',
  4901: 'Your wallet is not connected to BNB Chain. Switch network and try again.',
  // Raised when a connection prompt is already open - by far the most common
  // real-world failure, because clicking Connect twice is enough to trigger it.
  [-32002]: 'Your wallet already has a connection request open. Open your wallet and approve it.',
  [-32603]: 'Your wallet reported an internal error. Reopen it and try again.',
}

function readErrorCode(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined
  const { code } = error as { code?: unknown }
  return typeof code === 'number' ? code : undefined
}

function readErrorMessage(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined
  const { message } = error as { message?: unknown }
  return typeof message === 'string' && message.trim() ? message.trim() : undefined
}

function describeActivationError(error: unknown): string {
  if (error instanceof NoEthereumProviderError) {
    return 'No wallet extension detected (e.g. MetaMask). Install one and reload the page.'
  }
  if (error instanceof UnsupportedChainIdError) {
    return `Your wallet is on an unsupported network. Switch it to chain ID ${NETWORK_CHAIN_ID} and try again.`
  }
  if (error instanceof UserRejectedRequestErrorInjected) {
    return 'Connection request was rejected in your wallet.'
  }

  // Check the code before the message: a wallet's own wording for -32002 is
  // "Already processing eth_requestAccounts", which tells the user nothing about
  // what to actually do about it.
  const code = readErrorCode(error)
  if (code !== undefined && WALLET_ERROR_MESSAGES[code]) {
    return WALLET_ERROR_MESSAGES[code]
  }

  const message = readErrorMessage(error)
  if (message) return message

  return 'Something went wrong while connecting. Please try again.'
}

// Wraps web3-react's `activate()` with error handling - previously this was called with no
// .catch() anywhere in the app, so a rejected activation (wrong network, no wallet installed,
// user closed the MetaMask popup, etc.) failed completely silently: the Connect button just
// sat there looking like nothing happened, with no indication of what went wrong or why.
export default function useWalletActivate(): (connector: AbstractConnector) => void {
  const { activate } = useWeb3React()
  const addPopup = useAddPopup()

  return useCallback(
    (connector: AbstractConnector) => {
      activate(connector, undefined, true).catch((error: unknown) => {
        addPopup({ walletError: { message: describeActivationError(error) } }, `wallet-error-${Date.now()}`)
        // eslint-disable-next-line no-console
        console.error('Wallet activation failed:', error)
      })
    },
    [activate, addPopup]
  )
}
